<template>
  <div>
    <NuxtRouteAnnouncer />
    <NuxtPage />
    <NuxtPwaManifest />
  </div>
  <GlobalFooter />
  <ModalAlert />
  <GlobalToast />
  <!-- PWA 업데이트 안내 팝업은 노출하지 않는다 -->
  <!-- <PwaUpdatePrompt /> -->
</template>
<script setup>
import { useTracking } from "~/composables/useTracking";
import { useOptionStore } from "~/stores/option";
const { createTracking } = useTracking();

// 서버(크롤러 사전 렌더링, ViewController)가 넣은 head 태그 제거 — 화면이 같은 태그를 다시 넣으므로 중복 방지
document.querySelectorAll("[data-prerender]").forEach((el) => el.remove());

const router = useRouter();
const route = useRoute();
const optionStore = useOptionStore();

// 페이지별 canonical (쿼리 제외). 쿼리가 정식 주소인 페이지(qa/info 등)는 페이지에서 덮어쓴다.
const canonicalUrl = computed(() => `https://unsedang.com/view${route.path.replace(/\/+$/, "")}`);

// ✅ 모든 버튼/링크 클릭에 GA 이벤트 자동 전송 (data-ga-action으로 액션명 지정 가능)
function setupGlobalClickGA() {
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target.closest(
        "button, a[href], [role='button'], [data-ga], .confirm_btn, .login_btn, .charge, .tapmenu, .tab, .filterbtn_inner button, .ssam_inner, .ssam_inner2, .premium_card, .content_carousel_slide, .view_banner, .mission_btn, .go_gethstr, .term_view, [class*='btn']",
      );
      if (!el || el.closest("[data-ga-ignore]")) return;
      if (typeof window.gtag !== "function") return;

      const page =
        (router.currentRoute?.value?.path || window.location.pathname || "")
          .replace(/^\/view/, "")
          .replace(/\//g, "_") || "home";
      const action =
        el.getAttribute("data-ga-action") ||
        el.dataset?.gaAction ||
        el.id ||
        (typeof el.className === "string" ? el.className.split(/\s+/).find((c) => c && !/^\./.test(c)) : "") ||
        "click";
      const label = (el.textContent?.trim().slice(0, 80) || "").replace(/\s+/g, " ");

      window.gtag("event", "click", {
        event_category: page,
        event_label: action,
        event_action_label: label,
      });
    },
    true,
  );
}

onMounted(async () => {
  setupGlobalClickGA();
  checkUrlParam();
  // 옵션은 여기서 1회 조회해 store 에 보관한다 (화면들은 store 에서 꺼내 쓴다)
  optionStore.fetchOption();

  const mod = await import("~/utils/firebase.ts");
  await mod.listenForegroundMessages();

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data;

      // Firebase push-received 메시지 처리
      if (data?.messageType === "push-received" || data?.isFirebaseMessaging) {
        const link = data?.data?.link || data?.data?.notiLink;
        if (link) {
          // 절대 URL이면 경로만 추출
          const url = new URL(link);
          const path = url.pathname.replace(/^\/view/, "") || "/";
          navigateTo({ path: path });
        }
        return;
      }
    });
  }
});

// window.location 기준으로 통일 (route vs window 불일치 방지)
const checkUrlParam = () => {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "n_query",
    "n_rank",
    "n_keyword",
    "n_match",
  ];
  const hasUtmInUrl = utmKeys.some((key) => params.get(key));

  if (hasUtmInUrl) {
    const pageName = (window.location.pathname || "").replace(/^\/view/, "").replace(/\//g, "") || "home";
    createTracking("", pageName, "landing");
  }
};

useHead({
  script: [
    {
      src: "https://www.googletagmanager.com/gtag/js?id=G-293N6XQVE7",
      async: true,
    },
    {
      children: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        // 아래에서 UTM 정보를 붙여 page_view 를 직접 보내므로 자동 발송은 끈다 (이중 집계 방지)
        gtag('config', 'G-293N6XQVE7', { send_page_view: false });

        // UTM 파라미터 수집
        (function() {
          function getParam(name) {
            const params = new URLSearchParams(window.location.search);
            return params.get(name);
          }

          const utm_source = getParam('utm_source');
          const utm_medium = getParam('utm_medium');

          if (utm_source) localStorage.setItem('utm_source', utm_source);
          if (utm_medium) localStorage.setItem('utm_medium', utm_medium);

          const keyword = {
            utmCampaign: getParam('utm_campaign'),
            utmContent: getParam('utm_content'),
            utmTerm: getParam('utm_term'),
            nQuery: getParam('n_query'),
            nRank: getParam('n_rank'),
            nKeyword: getParam('n_keyword'),
            nMatch: getParam('n_match'),
          };

          const stored = localStorage.getItem('keyword')

          // ✅ 새로운 값이 있으면 갱신
          const hasNewValue = Object.values(keyword).some(v => v !== null);
          if (hasNewValue) {
            localStorage.setItem('keyword', JSON.stringify(keyword));
          } else if (!stored) {
            // ✅ 처음 한 번만 저장
            localStorage.setItem('keyword', JSON.stringify(keyword));
          }

          // page_view 커스텀 전송
          gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
          campaign_source: utm_source,
          campaign_medium: utm_medium
          });
        })();
      `,
      type: "text/javascript",
    },
    {
      children: `
        window.addEventListener('load', function() {
            var across_adn_contain = new fn_across_adn_contain();
            var across_adn_param = [];
            across_adn_param = {"ut":"Home","ui":"109459"};
            across_adn_contain.init(across_adn_param);
        });
      `,
      type: "text/javascript",
    },
    {
      children: `
        (function() {
          if(!window.wcs_add) window.wcs_add = {};
          window.wcs_add["wa"] = "s_265c03905dde";
          if(!window._nasa) window._nasa = {};

          var script = document.createElement('script');
          script.src = '//wcs.naver.net/wcslog.js';
          script.onload = function() {
            if(window.wcs) {
              wcs.inflow();
              wcs_do();
            }
          };
          document.head.appendChild(script);
        })();
      `,
      type: "text/javascript",
    },
  ],
  link: [{ rel: "canonical", href: canonicalUrl }],
  meta: [
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "운세당" },
    {
      property: "og:image",
      content: "https://storage.googleapis.com/bk-r-unsedang/UNSEDANG_OG_IMEANG/unsedang_og_all.png",
    },
    // 페이지별 meta는 각 페이지에서 useSeoMeta로 덮어쓰기
  ],
});
</script>
